import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
const newsletterSchema = z.object({
  email: z.string().email({
    message: "Bitte geben Sie eine gültige E-Mail-Adresse ein"
  }),
  name: z.string().optional()
});
type NewsletterFormData = z.infer<typeof newsletterSchema>;
interface NewsletterCTAProps {
  variant?: "inline" | "full" | "hero";
  compact?: boolean;
  className?: string;
}
const NewsletterCTA = ({
  variant = "full",
  compact = false,
  className = ""
}: NewsletterCTAProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const {
    toast
  } = useToast();
  const form = useForm<NewsletterFormData>({
    resolver: zodResolver(newsletterSchema),
    defaultValues: {
      email: "",
      name: ""
    }
  });
  const onSubmit = async (data: NewsletterFormData) => {
    setIsSubmitting(true);
    try {
      const {
        error
      } = await supabase.functions.invoke('newsletter-subscribe', {
        body: data
      });
      if (error) throw error;
      setIsSuccess(true);
      form.reset();
      toast({
        title: "Erfolgreich angemeldet! 🎉",
        description: "Vielen Dank für Ihre Anmeldung. Sie erhalten in Kürze eine Bestätigungs-E-Mail."
      });
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({
        title: "Fehler bei der Anmeldung",
        description: "Bitte versuchen Sie es später erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Hero variant - compact inline form
  if (variant === "hero") {
    return <div className="mt-8 max-w-md mx-auto">
        <div className="flex items-center justify-center gap-2 text-foreground/70 mb-3">
          <Mail className="w-4 h-4 text-accent" />
          <span className="small-text">Praxis-Tipps per Mail?</span>
        </div>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col sm:flex-row gap-2 items-stretch">
          <Input {...form.register("email")} type="email" placeholder="Ihre E-Mail" className="bg-background/50 border-accent/30 focus:border-accent w-full h-10" disabled={isSubmitting || isSuccess} />
          <Button type="submit" variant="outline" disabled={isSubmitting || isSuccess} className="h-10 whitespace-nowrap w-full sm:w-auto border-accent/50 text-foreground hover:bg-accent/10 hover:text-foreground">
            {isSuccess ? <CheckCircle2 className="w-4 h-4" /> : "Absenden"}
          </Button>
        </form>
        {form.formState.errors.email && <p className="text-destructive text-xs mt-1">{form.formState.errors.email.message}</p>}
      </div>;
  }

  // Inline variant - simple horizontal layout
  if (variant === "inline") {
    return <div className="py-8 px-4 bg-accent/5 rounded-lg">
        <div className="max-w-2xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center">
                <Mail className="w-5 h-5 text-accent" />
              </div>
              <span className="body-text font-medium text-foreground">Newsletter abonnieren</span>
            </div>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex gap-2 flex-1 w-full sm:w-auto">
              <Input {...form.register("email")} type="email" placeholder="Ihre E-Mail-Adresse" className="flex-1" disabled={isSubmitting || isSuccess} />
              <Button type="submit" disabled={isSubmitting || isSuccess} className="bg-accent text-accent-foreground hover:bg-accent/90" aria-label="Newsletter abonnieren">
                {isSuccess ? <CheckCircle2 className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
              </Button>
            </form>
          </div>
        </div>
      </div>;
  }

  // Full variant - comprehensive newsletter section
  return <section className="py-16 sm:py-20 px-4">
      <div className="container mx-auto max-w-4xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-accent/10 rounded-full mb-6">
            <Mail className="w-8 h-8 text-accent" />
          </div>
          <h2 className="heading-2 mb-4 text-foreground">
            Bleiben Sie auf dem Laufenden
          </h2>
          <p className="body-text text-foreground/60 max-w-2xl mx-auto mb-2">
            Versicherungstipps, Cyber-Warnungen & exklusive Angebote für Praxen
          </p>
          <p className="small-text text-foreground/50">
            Maximal 2 E-Mails pro Monat • Jederzeit abmelden
          </p>
        </div>

        {isSuccess ? <div className="max-w-md mx-auto text-center py-8 animate-fade-in">
            <CheckCircle2 className="w-16 h-16 text-accent mx-auto mb-4" />
            <h3 className="heading-3 text-foreground mb-2">Vielen Dank!</h3>
            <p className="body-text text-foreground/70">
              Sie erhalten in Kürze eine Bestätigungs-E-Mail.
            </p>
          </div> : <form onSubmit={form.handleSubmit(onSubmit)} className="max-w-2xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Input {...form.register("email")} type="email" placeholder="Ihre E-Mail-Adresse*" className="flex-1 h-12" disabled={isSubmitting} />
              <Input {...form.register("name")} type="text" placeholder="Name (optional)" className="flex-1 h-12" disabled={isSubmitting} />
              <Button type="submit" size="lg" disabled={isSubmitting} className="bg-accent text-accent-foreground hover:bg-accent/90 hover:shadow-[0_0_20px_rgba(139,195,74,0.3)] whitespace-nowrap">
                {isSubmitting ? "Wird gesendet..." : "Jetzt anmelden"}
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
            {form.formState.errors.email && <p className="text-destructive text-sm mb-2">{form.formState.errors.email.message}</p>}
            <div className="text-center">
              <p className="small-text text-foreground/50">
                ✓ Bereits 500+ Praxen vertrauen unserem Newsletter
              </p>
              <p className="small-text text-foreground/50 mt-2">
                Wir halten uns an die
              </p>
              <p className="small-text text-foreground/50 mt-1 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
                <a href="/datenschutz" className="text-accent hover:underline">
                  Datenschutzbestimmungen
                </a>
                <span className="hidden sm:inline">|</span>
                <a href="/newsletter/abmelden" className="text-accent hover:underline">
                  Newsletter abmelden
                </a>
              </p>
            </div>
          </form>}
      </div>
    </section>;
};
export default NewsletterCTA;